import { allowedImageMimeTypesForAnnouncementUpload } from 'globalConstants';
import Language from 'models/language';
import { appRoles } from 'services/Auth/appRoles';
import { getLocaleDateTimeString } from 'utils/datetime';
import { htmlRemoveImages, removeHtmlTags, replaceEscapeCharacters } from 'utils/html';
import { SubscriptionTypes } from 'utils/subscription';

//
// Enums: the numbers are the wire values of the API (enums travel as numbers)
//
export enum AnnouncementType {
  ReleaseNotes = 1,
  NewFeature = 2,
  News = 3,
}

export enum AnnouncementState {
  Draft = 0,
  Published = 1,
  Withdrawn = 2,
}

//never stored: worked out by the API from the state and the two dates
export enum AnnouncementStatus {
  Draft = 0,
  Scheduled = 1,
  Live = 2,
  Expired = 3,
  Withdrawn = 4,
}

//
// Limits that the editor enforces, the same as the checks of the API manager
//
export const announcementTitleMaxLength = 80;
export const announcementSummaryMaxLength = 140;
export const announcementBodyMaxLength = 32000;
export const announcementImageMaxSize = 5 * 1024 * 1024;

export const announcementImageTypeError = 'Only PNG, JPEG, GIF and WebP images can be used.';
export const announcementImageSizeError = 'The image is larger than 5 MB.';

const announcementImageContentTypes = Object.keys(allowedImageMimeTypesForAnnouncementUpload);

//the reason an image cannot be uploaded, or undefined when it can
export function checkAnnouncementImage(file: Blob): string | undefined {
  if (!announcementImageContentTypes.includes(file.type)) return announcementImageTypeError;
  if (file.size > announcementImageMaxSize) return announcementImageSizeError;

  return undefined;
}

//the language that every announcement has; the others are translations of it
export const announcementEnglishCode = 'en';
export const announcementTranslationCodes: string[] = ['nl', 'de'];
export const announcementLanguageCodes: string[] = [announcementEnglishCode, ...announcementTranslationCodes];

/**
 * Where the text of a Dutch or German language stands: no text (readers get English), the English
 * changed after the text was translated or approved, AI text that nobody approved (readers get English),
 * or approved or written by a person. Publishing waits for English changed and To review.
 */
export enum AnnouncementTranslationState {
  Empty = 'empty',
  EnglishChanged = 'englishChanged',
  ToReview = 'toReview',
  Done = 'done',
}

//
// English texts for the Tenant Admin (staff) screens. The reader texts are in the announcements locale file.
//
export const announcementTypeNames: Record<AnnouncementType, string> = {
  [AnnouncementType.ReleaseNotes]: 'Release notes',
  [AnnouncementType.NewFeature]: 'New feature',
  [AnnouncementType.News]: 'News',
};

export const announcementStatusNames: Record<AnnouncementStatus, string> = {
  [AnnouncementStatus.Draft]: 'Draft',
  [AnnouncementStatus.Scheduled]: 'Scheduled',
  [AnnouncementStatus.Live]: 'Live',
  [AnnouncementStatus.Expired]: 'Expired',
  [AnnouncementStatus.Withdrawn]: 'Withdrawn',
};

export const announcementLanguageNames: Record<string, string> = {
  en: 'English',
  nl: 'Dutch',
  de: 'German',
};

export function getAnnouncementLanguageName(languageCode: string): string {
  return announcementLanguageNames[languageCode] ?? languageCode;
}

//the language id of a code in the language list, or 0 when the list does not have the code
export function getAnnouncementLanguageId(languageCode: string, languages: Language[]): number {
  return languages.find((l) => l.code.toLowerCase() === languageCode)?.languageId ?? 0;
}

//
// Audience: the roles, plans and languages an announcement can be addressed to
//

//the same role names as the role texts in translation.json
export const announcementAudienceRoles: { role: appRoles; name: string }[] = [
  { role: appRoles.User, name: 'User' },
  { role: appRoles.Manager, name: 'Manager' },
  { role: appRoles.Admin, name: 'Admin' },
  { role: appRoles.OrgAdmin, name: 'Organization Admin' },
  { role: appRoles.Consultant, name: 'Consultant' },
  { role: appRoles.LicenseManager, name: 'License Manager' },
];

export const announcementAudienceDefaultRoles: number[] = [
  appRoles.Manager,
  appRoles.Admin,
  appRoles.OrgAdmin,
  appRoles.Consultant,
];

export const announcementAudiencePlans: { plan: SubscriptionTypes; name: string }[] = [
  { plan: SubscriptionTypes.Basic, name: 'Basic' },
  { plan: SubscriptionTypes.Business, name: 'Business' },
  { plan: SubscriptionTypes.Premium, name: 'Premium' },
  { plan: SubscriptionTypes.NIS2, name: 'NIS2' },
  { plan: SubscriptionTypes.Portal, name: 'Portal' },
];

export const announcementAudienceLanguages: { code: string; name: string }[] = announcementLanguageCodes.map(
  (code) => ({ code, name: announcementLanguageNames[code] }),
);

//
// A single line that says who an announcement reaches, such as "Manager, Admin · Premium · NL".
// An undefined plan or language list means all of them.
//
const getAudiencePlanText = (plans: number[] | undefined): string =>
  plans === undefined
    ? 'All plans'
    : announcementAudiencePlans
        .filter((p) => plans.includes(p.plan))
        .map((p) => p.name)
        .join(', ') || 'No plan';

const getAudienceLanguageText = (languages: string[] | undefined): string =>
  languages === undefined ? 'All languages' : languages.map((code) => code.toUpperCase()).join(', ') || 'No language';

export function getAnnouncementAudienceSummary(
  roles: number[],
  plans: number[] | undefined,
  languages: string[] | undefined,
): string {
  const roleText =
    roles.length > 0
      ? announcementAudienceRoles
          .filter((r) => roles.includes(r.role))
          .map((r) => r.name)
          .join(', ')
      : 'No role';

  return `${roleText} · ${getAudiencePlanText(plans)} · ${getAudienceLanguageText(languages)}`;
}

//"Not shown to: User, License Manager", or undefined when the announcement goes to every role
export function getAnnouncementNotShownToText(roles: number[]): string | undefined {
  const excluded = announcementAudienceRoles.filter((r) => !roles.includes(r.role)).map((r) => r.name);

  return excluded.length > 0 && excluded.length < announcementAudienceRoles.length
    ? `Not shown to: ${excluded.join(', ')}`
    : undefined;
}

/**
 * A moment as the editor shows it everywhere: the date with a short month and the time in 24 hours,
 * as the time field shows it, such as "Oct 28, 2026 00:28".
 */
export function formatAnnouncementMoment(date: Date): string {
  return getLocaleDateTimeString(
    date,
    { year: 'numeric', month: 'short', day: 'numeric' },
    { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  );
}

/**
 * A publish moment that is not in the future, of an item that is not published: POST publish makes it
 * now, so it means "when you publish". A new item starts at the minute the editor opened.
 */
export function publishesWhenPublished(publishOn: Date | undefined, isPublished: boolean): boolean {
  return !isPublished && publishOn !== undefined && publishOn.getTime() <= Date.now();
}

//an expiry moment that is not after the publish moment: readers would never see the announcement
export function isExpiryBeforePublish(publishOn: Date | undefined, expiresOn: Date | undefined): boolean {
  return publishOn !== undefined && expiresOn !== undefined && expiresOn.getTime() <= publishOn.getTime();
}

//the title of the delete confirm, in the editor and in the list
export const announcementDeleteTitle = 'Delete announcement?';

//`isVisibleToReaders`: a live or scheduled item, which the list withdraws before it deletes it
export function getAnnouncementDeleteText(title: string, isVisibleToReaders: boolean): string {
  return (
    `"${title || 'This announcement'}" is deleted for good, with its translations, images and read state. ` +
    (isVisibleToReaders ? 'It is withdrawn first, so readers no longer see it. ' : '') +
    'This cannot be undone.'
  );
}

//true when both lists hold the same values in any order, or both are undefined
export function isSameList<T>(left: T[] | undefined, right: T[] | undefined): boolean {
  if (left === undefined || right === undefined) return left === right;
  if (left.length !== right.length) return false;

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();

  return sortedLeft.every((value, index) => value === sortedRight[index]);
}

//
// A language version of an announcement
//
export class AnnouncementTranslation {
  languageId: number;

  //en, nl or de; empty when the API sent no language code (see getAnnouncementTranslationCode)
  languageCode: string;

  title: string;

  summary: string;

  body: string;

  /**
   * AI text that staff have not reviewed; publishing is blocked while a row has it. The editor sets it
   * on the text of the translate call and keeps it through edits by hand; Mark as reviewed clears it.
   * Sent with a save: the API keeps it true for text it knows as AI, whatever the request says.
   */
  isMachineTranslated: boolean;

  //Approve in the editor since the last save; the save sends it, and the API keeps the English the row
  //is for and records the review of AI text
  isReviewed: boolean;

  //read-only, set by the API: the English changed after this row was translated or approved. Not part
  //of `isEqual`.
  isSourceChanged: boolean;

  /**
   * The English this text was translated from or approved for, as a reader reads it; the editor sets it
   * (`Announcement.setTranslationSources`, translate, Approve). Undefined for English, for an empty row,
   * and for a row the API says is for an older English. Not part of `isEqual`.
   */
  source?: IAnnouncementSourceText;

  //read-only, set by the API: the last review of this row. Not part of `isEqual`.
  reviewedOn?: Date;

  reviewedById?: string;

  //set by the API when this row was last saved
  modified?: Date;

  constructor() {
    this.languageId = 0;
    this.languageCode = '';
    this.title = '';
    this.summary = '';
    this.body = '';
    this.isMachineTranslated = false;
    this.isReviewed = false;
    this.isSourceChanged = false;
  }

  //true when there is anything in this language
  hasContent(): boolean {
    return this.title.trim().length > 0 || this.summary.trim().length > 0 || this.hasBody();
  }

  //the test of `HasContent` in the API: the text without tags and with entities such as `&nbsp;`
  //decoded is not blank, or the body holds an image. An empty editor leaves `<p>&nbsp;</p>`.
  hasBody(): boolean {
    return replaceEscapeCharacters(removeHtmlTags(this.body)).trim().length > 0 || /<img\b/i.test(this.body);
  }

  isEqual(item: AnnouncementTranslation): boolean {
    if (this.languageId !== item.languageId) return false;
    if (this.languageCode !== item.languageCode) return false;
    if (this.title !== item.title) return false;
    if (this.summary !== item.summary) return false;
    if (this.body !== item.body) return false;
    if (this.isMachineTranslated !== item.isMachineTranslated) return false;
    if (this.isReviewed !== item.isReviewed) return false;
    if (this.modified?.getTime() !== item.modified?.getTime()) return false;

    return true;
  }

  //the fields a person edits, with the flag of AI text and the review of the form
  copyTextFrom(source: Readonly<AnnouncementEditableText>): void {
    this.title = source.title;
    this.summary = source.summary;
    this.body = source.body;
    this.isMachineTranslated = source.isMachineTranslated;
    this.isReviewed = source.isReviewed;
  }

  clone(): AnnouncementTranslation {
    const output = new AnnouncementTranslation();
    output.languageId = this.languageId;
    output.languageCode = this.languageCode;
    output.title = this.title;
    output.summary = this.summary;
    output.body = this.body;
    output.isMachineTranslated = this.isMachineTranslated;
    output.isReviewed = this.isReviewed;
    output.isSourceChanged = this.isSourceChanged;
    output.source = this.source;
    output.reviewedOn = this.reviewedOn;
    output.reviewedById = this.reviewedById;
    output.modified = this.modified;

    return output;
  }
}

//the code of a translation row: the code of its languageId in the language list, else the code the API sent
export function getAnnouncementTranslationCode(translation: AnnouncementTranslation, languages: Language[]): string {
  return languages.find((l) => l.languageId === translation.languageId)?.code.toLowerCase() ?? translation.languageCode;
}

//the title, summary and text of a language as a reader reads them: markup and runs of whitespace do not count
export interface IAnnouncementSourceText {
  title: string;
  summary: string;
  body: string;
}

export type AnnouncementTextField = keyof IAnnouncementSourceText;

export const announcementTextFields: AnnouncementTextField[] = ['title', 'summary', 'body'];

const toComparableText = (text: string): string => text.replace(/\s+/g, ' ').trim();

//the block elements of a body: each one breaks the words around it, as in the text a reader reads
const blockTagPattern = /<\/?(p|div|h[1-6]|li|ul|ol|br|tr|td|th|table|blockquote|pre|hr|figure|figcaption)\b[^>]*>/gi;

const parseBody = (html: string): Document =>
  new DOMParser().parseFromString(html.replace(blockTagPattern, ' '), 'text/html');

//the text of a body as a reader reads it: markup, formatting and link addresses are no part of it
export const getAnnouncementPlainText = (html: string): string =>
  toComparableText(parseBody(html).body.textContent ?? '');

/**
 * The body as the API compares it (`ContentHash`): its text and its images with their alt text. A
 * change of formatting, a heading or a link address is no change of the English.
 */
const toComparableBody = (html: string): string => {
  const doc = parseBody(html);
  const images = Array.from(doc.images, (img) => `${img.getAttribute('src') ?? ''} ${img.getAttribute('alt') ?? ''}`);

  return toComparableText([doc.body.textContent ?? '', ...images].join(' '));
};

//The editor reads the English on every render, in several places; its text is worked out once per English
let lastSourceText: { key: string; value: IAnnouncementSourceText } | undefined;

export const toAnnouncementSourceText = (translation: Readonly<AnnouncementTranslation>): IAnnouncementSourceText => {
  const key = [translation.title, translation.summary, translation.body].join('\u0001');
  if (lastSourceText?.key !== key) {
    lastSourceText = {
      key,
      value: {
        title: toComparableText(translation.title),
        summary: toComparableText(translation.summary),
        body: toComparableBody(translation.body),
      },
    };
  }

  return lastSourceText.value;
};

//returned by Announcement.english when there is no English row; frozen, so a write to it throws
const emptyEnglishTranslation: Readonly<AnnouncementTranslation> = Object.freeze(
  Object.assign(new AnnouncementTranslation(), { languageCode: announcementEnglishCode }),
);

//why one language of the translate call failed: the stable codes of the API
//(AIHtmlTranslationErrorCodes)
export const announcementTranslationErrorCodes = [
  'unsupported',
  'empty',
  'changedImageOrLink',
  'tooLong',
  'unsafeMarkup',
  'timeout',
  'failed',
] as const;

export type AnnouncementTranslationErrorCode = (typeof announcementTranslationErrorCodes)[number];

//a code that is not in the list counts as failed
export function toAnnouncementTranslationErrorCode(code: string): AnnouncementTranslationErrorCode {
  return announcementTranslationErrorCodes.find((known) => known === code) ?? 'failed';
}

const announcementTranslationErrorTexts: Record<AnnouncementTranslationErrorCode, (languageName: string) => string> = {
  unsupported: (languageName) => `${languageName} is not a language the AI translation supports.`,
  empty: (languageName) => `The AI returned no ${languageName} text. Try again, or write it yourself.`,
  changedImageOrLink: (languageName) =>
    `The ${languageName} translation changed an image or a link, so it was not used. Try again, or write it yourself.`,
  tooLong: (languageName) =>
    `The ${languageName} translation came out longer than an announcement allows. Shorten the English text and translate again, or write it yourself.`,
  unsafeMarkup: (languageName) =>
    `The ${languageName} translation changed the formatting of the text, so it was not used. Try again, or write it yourself.`,
  timeout: (languageName) => `The ${languageName} translation took too long. Try again, or write it yourself.`,
  failed: (languageName) => `The ${languageName} translation failed. Try again, or write it yourself.`,
};

//the fields the translate call names in `errorField` (AnnouncementTranslationResultDTO), with their limit
const announcementTranslationFields: Record<string, { name: string; max: number }> = {
  title: { name: 'title', max: announcementTitleMaxLength },
  summary: { name: 'summary', max: announcementSummaryMaxLength },
  body: { name: 'text', max: announcementBodyMaxLength },
};

//the message the editor shows for a language whose translation failed
export function getAnnouncementTranslationErrorText(
  errorCode: AnnouncementTranslationErrorCode,
  errorField: string | undefined,
  languageName: string,
): string {
  const field = errorField ? announcementTranslationFields[errorField] : undefined;
  if (errorCode === 'tooLong' && field) {
    return (
      `The ${languageName} ${field.name} came out longer than the ${field.max} characters it may have. Shorten ` +
      `the English ${field.name} and translate again, or write the ${languageName} text yourself.`
    );
  }

  return announcementTranslationErrorTexts[errorCode](languageName);
}

//one language of the translate call: the AI draft, or the reason the language could not be translated
export class AnnouncementTranslationResult {
  //as requested, trimmed and lower case
  languageCode: string;

  //0 for a code that is not an ISOPlanner language
  languageId: number;

  //the texts are empty when the language failed; the summary also when the English summary is empty
  title: string;

  summary: string;

  body: string;

  //set when the language failed
  errorCode?: AnnouncementTranslationErrorCode;

  //the field the failure is about, such as the one that came out too long: title, summary or body
  errorField?: string;

  constructor() {
    this.languageCode = '';
    this.languageId = 0;
    this.title = '';
    this.summary = '';
    this.body = '';
  }
}

//the fields of a language a person edits, with the flag of AI text and the review of the form
export type AnnouncementEditableText = Pick<
  AnnouncementTranslation,
  'title' | 'summary' | 'body' | 'isMachineTranslated' | 'isReviewed'
>;

//the fields of `Announcement` a person edits, as plain values (dates as milliseconds), so they can be kept as JSON
export interface IAnnouncementEditableFields {
  announcementType: AnnouncementType;
  publishOn?: number;
  expiresOn?: number;
  coverImageId?: string;
  coverImageUrl?: string;
  targetRoles: number[];
  targetSubscriptionTypes?: number[];
  targetLanguages?: string[];
  trans: (AnnouncementEditableText & { languageCode: string; languageId: number })[];
}

//
// The announcement as staff edit it (admin model)
//
export class Announcement {
  //0 until the first save
  announcementId: number;

  announcementType: AnnouncementType;

  state: AnnouncementState;

  //read-only, worked out by the API
  status: AnnouncementStatus;

  publishOn?: Date;

  expiresOn?: Date;

  coverImageId?: string;

  coverImageUrl?: string;

  trans: AnnouncementTranslation[];

  targetRoles: number[];

  //undefined: all plans, so new plans are included automatically
  targetSubscriptionTypes?: number[];

  //undefined: all languages
  targetLanguages?: string[];

  readCount: number;

  created?: Date;

  createdById?: string;

  //also the conflict check of a save: sent back unchanged
  modified?: Date;

  modifiedById?: string;

  constructor() {
    this.announcementId = 0;
    this.announcementType = AnnouncementType.NewFeature;
    this.state = AnnouncementState.Draft;
    this.status = AnnouncementStatus.Draft;
    this.trans = [];
    this.targetRoles = [...announcementAudienceDefaultRoles];
    this.readCount = 0;
  }

  isNew(): boolean {
    return this.announcementId === 0;
  }

  getTranslation(languageCode: string): AnnouncementTranslation | undefined {
    return this.trans.find((tr) => tr.languageCode === languageCode);
  }

  //the English version, for reading only: an empty frozen row when there is none. Write through ensureTranslation.
  get english(): Readonly<AnnouncementTranslation> {
    return this.getTranslation(announcementEnglishCode) ?? emptyEnglishTranslation;
  }

  //sets the code of every row from its languageId and adds the English row when it is missing
  applyLanguages(languages: Language[]): void {
    for (const translation of this.trans) {
      translation.languageCode = getAnnouncementTranslationCode(translation, languages);
    }
    this.ensureTranslation(announcementEnglishCode, getAnnouncementLanguageId(announcementEnglishCode, languages));
  }

  //returns the row of a language, adding an empty one when there is none yet
  ensureTranslation(languageCode: string, languageId: number): AnnouncementTranslation {
    let translation = this.getTranslation(languageCode);
    if (!translation) {
      translation = new AnnouncementTranslation();
      translation.languageCode = languageCode;
      translation.languageId = languageId;
      this.trans.push(translation);
    }

    return translation;
  }

  //the translations that are drafted by AI and not yet reviewed
  getUnreviewedTranslations(): AnnouncementTranslation[] {
    return this.trans.filter((tr) => tr.isMachineTranslated && tr.hasContent());
  }

  //where the text of a Dutch or German language stands; English is the source and always Done
  getTranslationState(languageCode: string): AnnouncementTranslationState {
    const translation = this.getTranslation(languageCode);
    if (!translation?.hasContent()) return AnnouncementTranslationState.Empty;
    if (languageCode === announcementEnglishCode) return AnnouncementTranslationState.Done;
    if (this.getChangedSourceFields(languageCode)) return AnnouncementTranslationState.EnglishChanged;
    if (translation.isMachineTranslated) return AnnouncementTranslationState.ToReview;

    return AnnouncementTranslationState.Done;
  }

  //the English as a reader reads it, as a translation keeps it in `source`
  getEnglishSource(): IAnnouncementSourceText {
    return toAnnouncementSourceText(this.english);
  }

  /**
   * After a load or a save: every Dutch and German row with text is for the English as it is now,
   * unless the API says it is for an older English (`isSourceChanged`).
   */
  setTranslationSources(): void {
    const english = this.getEnglishSource();
    for (const translation of this.trans) {
      const isTranslation = translation.languageCode !== announcementEnglishCode && translation.hasContent();
      translation.source = isTranslation && !translation.isSourceChanged ? english : undefined;
    }
  }

  /**
   * The fields of the English that differ from the English a Dutch or German text was translated from
   * or approved for, or undefined when the English did not change. Empty when the API says the English
   * changed but the form does not know which field: it was loaded after that change.
   */
  getChangedSourceFields(languageCode: string): AnnouncementTextField[] | undefined {
    const translation = this.getTranslation(languageCode);
    if (languageCode === announcementEnglishCode || !translation?.hasContent()) return undefined;

    const source = translation.source;
    if (!source) return translation.isSourceChanged ? [] : undefined;

    const english = this.getEnglishSource();
    const fields = announcementTextFields.filter((field) => english[field] !== source[field]);

    return fields.length > 0 ? fields : undefined;
  }

  getAudienceSummary(): string {
    return getAnnouncementAudienceSummary(this.targetRoles, this.targetSubscriptionTypes, this.targetLanguages);
  }

  //AI translates from the English, so it needs the English title and text
  canTranslate(): boolean {
    return this.english.title.trim().length > 0 && this.english.hasBody();
  }

  //the fields a person edits, as plain values: the recovery copy in the browser keeps these
  getEditableFields(): IAnnouncementEditableFields {
    return {
      announcementType: this.announcementType,
      publishOn: this.publishOn?.getTime(),
      expiresOn: this.expiresOn?.getTime(),
      coverImageId: this.coverImageId,
      coverImageUrl: this.coverImageUrl,
      targetRoles: [...this.targetRoles],
      targetSubscriptionTypes: this.targetSubscriptionTypes ? [...this.targetSubscriptionTypes] : undefined,
      targetLanguages: this.targetLanguages ? [...this.targetLanguages] : undefined,
      trans: this.trans.map((tr) => ({
        languageCode: tr.languageCode,
        languageId: tr.languageId,
        title: tr.title,
        summary: tr.summary,
        body: tr.body,
        isMachineTranslated: tr.isMachineTranslated,
        isReviewed: tr.isReviewed,
      })),
    };
  }

  /**
   * Puts `fields` over this item. A row keeps `modified` and the review the API recorded; a row that
   * `fields` does not have is emptied, as it was in the form.
   */
  setEditableFields(fields: IAnnouncementEditableFields): void {
    this.announcementType = fields.announcementType;
    this.publishOn = fields.publishOn !== undefined ? new Date(fields.publishOn) : undefined;
    this.expiresOn = fields.expiresOn !== undefined ? new Date(fields.expiresOn) : undefined;
    this.coverImageId = fields.coverImageId;
    this.coverImageUrl = fields.coverImageUrl;
    this.targetRoles = [...fields.targetRoles];
    this.targetSubscriptionTypes = fields.targetSubscriptionTypes ? [...fields.targetSubscriptionTypes] : undefined;
    this.targetLanguages = fields.targetLanguages ? [...fields.targetLanguages] : undefined;
    for (const translation of this.trans) {
      if (!fields.trans.some((tr) => tr.languageCode === translation.languageCode)) {
        translation.copyTextFrom(new AnnouncementTranslation());
      }
    }
    for (const copied of fields.trans) {
      this.ensureTranslation(copied.languageCode, copied.languageId).copyTextFrom(copied);
    }
  }

  /**
   * A new announcement with the text, audience and schedule of this one, such as after this one was
   * deleted by someone else. The images went with this one, so they are left out. A publish moment that
   * has passed becomes `defaultPublishOn`: the new item publishes when it is published.
   */
  copyAsNew(defaultPublishOn: Date): { copy: Announcement; hasLeftOutImages: boolean } {
    const copy = new Announcement();
    copy.setEditableFields(this.getEditableFields());
    if (!copy.publishOn || copy.publishOn.getTime() <= Date.now()) copy.publishOn = defaultPublishOn;
    copy.coverImageId = undefined;
    copy.coverImageUrl = undefined;
    copy.trans.forEach((tr) => (tr.body = htmlRemoveImages(tr.body)));
    const hasLeftOutImages = !!this.coverImageId || this.trans.some((tr) => /<img\b/i.test(tr.body));

    return { copy, hasLeftOutImages };
  }

  //what stops the announcement from being saved; empty when it can be saved
  getValidationErrors(): string[] {
    const errors: string[] = [];
    if (this.english.title.trim().length === 0) errors.push('The English title is required');
    if (!this.english.hasBody()) errors.push('The English text is required');
    for (const translation of this.trans) {
      const name = getAnnouncementLanguageName(translation.languageCode);
      if (translation.title.length > announcementTitleMaxLength) {
        errors.push(`The ${name} title is longer than ${announcementTitleMaxLength} characters`);
      }
      if (translation.summary.length > announcementSummaryMaxLength) {
        errors.push(`The ${name} summary is longer than ${announcementSummaryMaxLength} characters`);
      }
      if (
        translation.languageCode !== announcementEnglishCode &&
        translation.hasContent() &&
        (translation.title.trim().length === 0 || !translation.hasBody())
      ) {
        errors.push(`The ${name} version needs a title and a text, or nothing at all`);
      }
    }
    if (this.targetRoles.length === 0) errors.push('Choose at least one role');
    if (this.targetSubscriptionTypes?.length === 0) errors.push('Choose at least one plan');
    if (this.targetLanguages?.length === 0) errors.push('Choose at least one language');

    return errors;
  }

  /**
   * Draft, Scheduled, Live, Expired or Withdrawn at `now`, worked out from the state and the dates as
   * the API does (`GetStatus`). Use it on the stored item: the API checks a write against that.
   */
  getStatusAt(now: Date): AnnouncementStatus {
    switch (this.state) {
      case AnnouncementState.Withdrawn:
        return AnnouncementStatus.Withdrawn;
      case AnnouncementState.Published:
        if (!this.publishOn || this.publishOn.getTime() > now.getTime()) return AnnouncementStatus.Scheduled;

        return this.expiresOn && this.expiresOn.getTime() <= now.getTime()
          ? AnnouncementStatus.Expired
          : AnnouncementStatus.Live;
      default:
        return AnnouncementStatus.Draft;
    }
  }

  //the languages of the audience without a translation that has content (ANNOUNCEMENT0011)
  getMissingTargetTranslations(): string[] {
    return (this.targetLanguages ?? []).filter((code) => !this.getTranslation(code)?.hasContent());
  }

  //ANNOUNCEMENT0011 of the API's `PreparePublish`
  private getMissingTranslationBlock(): string | undefined {
    const missing = this.getMissingTargetTranslations();
    if (missing.length === 0) return undefined;

    const names = missing.map((code) => getAnnouncementLanguageName(code));

    return `Add the translation for every language of the audience, or change the audience: ${names.join(', ')}`;
  }

  /**
   * The translation rules of the API's `PreparePublish` on a publish (ANNOUNCEMENT0011, then 0012),
   * then the editor's own: a translation made for an older English is approved or translated again first.
   */
  private getPublishTranslationBlock(): string | undefined {
    const missing = this.getMissingTranslationBlock();
    if (missing) return missing;

    const changed = announcementTranslationCodes.filter((code) => !!this.getChangedSourceFields(code));
    if (changed.length > 0) {
      const names = changed.map((code) => getAnnouncementLanguageName(code));

      return `The English changed. Approve or translate again: ${names.join(', ')}`;
    }

    const unreviewed = this.getUnreviewedTranslations();
    if (unreviewed.length > 0) {
      const names = unreviewed.map((tr) => getAnnouncementLanguageName(tr.languageCode));

      return `Approve the AI translation first: ${names.join(', ')}`;
    }

    return undefined;
  }

  /**
   * What stops POST publish of this item (a draft, withdrawn or expired item) at `now`, or undefined.
   * The rules of the API's `PreparePublish` on the way in, in its order: a publish moment before
   * `now` counts as `now`, so the expiry moment has to be later than both. The editor saves the
   * content first, so the translation rules apply to the content of the form.
   */
  getPublishBlock(now: Date): string | undefined {
    if (!this.publishOn) return 'Set the publish moment';
    if (this.expiresOn) {
      //an expiry before the publish moment is the mistake to name, also when that expiry has passed too
      if (isExpiryBeforePublish(this.publishOn, this.expiresOn)) {
        return 'The expiry moment must be after the publish moment';
      }
      if (this.expiresOn.getTime() <= now.getTime()) return 'The expiry moment has passed';
    }

    return this.getPublishTranslationBlock();
  }

  /**
   * What stops a save (PUT) of this item when `stored` is published, or undefined. The API runs the
   * publish rules without moving a past publish moment to now; it refuses an expiry moment that has
   * passed only when it changed, and a save that would show an expired item again (ANNOUNCEMENT0002).
   * It stores an unreviewed AI translation (no ANNOUNCEMENT0012): readers of that language see the
   * English text until the review.
   */
  getPublishedSaveBlock(now: Date, stored: Announcement): string | undefined {
    const isShownAgain = !this.expiresOn || this.expiresOn.getTime() > now.getTime();
    if (stored.getStatusAt(now) === AnnouncementStatus.Expired && isShownAgain) {
      return 'The announcement has expired: a save keeps it hidden. Use Publish again to show it with a new expiry moment';
    }
    if (!this.publishOn) return 'Set the publish moment';
    if (this.expiresOn) {
      if (isExpiryBeforePublish(this.publishOn, this.expiresOn)) {
        return 'The expiry moment must be after the publish moment';
      }
      const isExpiryChanged = this.expiresOn.getTime() !== stored.expiresOn?.getTime();
      if (isExpiryChanged && this.expiresOn.getTime() <= now.getTime()) return 'The expiry moment has passed';
    }

    return this.getMissingTranslationBlock();
  }

  //a copy without the translations that have nothing in them (the editor adds a row when a tab is opened).
  //This is what is saved, and what is compared to see whether the form changed.
  withoutEmptyTranslations(): Announcement {
    const output = this.clone();
    output.trans = output.trans.filter((tr) => tr.languageCode === announcementEnglishCode || tr.hasContent());

    return output;
  }

  isEqual(item: Announcement): boolean {
    if (this.announcementId !== item.announcementId) return false;
    if (this.announcementType !== item.announcementType) return false;
    if (this.state !== item.state) return false;
    if (this.publishOn?.getTime() !== item.publishOn?.getTime()) return false;
    if (this.expiresOn?.getTime() !== item.expiresOn?.getTime()) return false;
    if (this.coverImageId !== item.coverImageId) return false;
    if (this.modified?.getTime() !== item.modified?.getTime()) return false;
    if (!isSameList(this.targetRoles, item.targetRoles)) return false;
    if (!isSameList(this.targetSubscriptionTypes, item.targetSubscriptionTypes)) return false;
    if (!isSameList(this.targetLanguages, item.targetLanguages)) return false;
    if (this.trans.length !== item.trans.length) return false;
    for (const translation of this.trans) {
      const other = item.getTranslation(translation.languageCode);
      if (!other || !translation.isEqual(other)) return false;
    }

    return true;
  }

  clone(): Announcement {
    const output = new Announcement();
    output.announcementId = this.announcementId;
    output.announcementType = this.announcementType;
    output.state = this.state;
    output.status = this.status;
    output.publishOn = this.publishOn;
    output.expiresOn = this.expiresOn;
    output.coverImageId = this.coverImageId;
    output.coverImageUrl = this.coverImageUrl;
    output.trans = this.trans.map((tr) => tr.clone());
    output.targetRoles = [...this.targetRoles];
    output.targetSubscriptionTypes = this.targetSubscriptionTypes ? [...this.targetSubscriptionTypes] : undefined;
    output.targetLanguages = this.targetLanguages ? [...this.targetLanguages] : undefined;
    output.readCount = this.readCount;
    output.created = this.created;
    output.createdById = this.createdById;
    output.modified = this.modified;
    output.modifiedById = this.modifiedById;

    return output;
  }
}

//
// A row of the staff list
//
export class AnnouncementAdminListItem {
  announcementId: number;

  announcementType: AnnouncementType;

  state: AnnouncementState;

  status: AnnouncementStatus;

  //the English title
  title: string;

  //the English summary
  summary?: string;

  publishOn?: Date;

  expiresOn?: Date;

  languageCodes: string[];

  //the languages that hold an AI draft that is not reviewed yet
  unreviewedLanguageCodes: string[];

  targetRoles: number[];

  targetSubscriptionTypes?: number[];

  targetLanguages?: string[];

  readCount: number;

  modified?: Date;

  constructor() {
    this.announcementId = 0;
    this.announcementType = AnnouncementType.NewFeature;
    this.state = AnnouncementState.Draft;
    this.status = AnnouncementStatus.Draft;
    this.title = '';
    this.languageCodes = [];
    this.unreviewedLanguageCodes = [];
    this.targetRoles = [];
    this.readCount = 0;
  }

  getAudienceText(): string {
    return getAnnouncementAudienceSummary(this.targetRoles, this.targetSubscriptionTypes, this.targetLanguages);
  }

  //a short form of the audience for a table cell, such as "4 roles · All plans · All languages"
  getAudienceShortText(): string {
    const roleCount = announcementAudienceRoles.length;
    const roleNames = announcementAudienceRoles.filter((r) => this.targetRoles.includes(r.role)).map((r) => r.name);
    const planNames = announcementAudiencePlans
      .filter((p) => this.targetSubscriptionTypes?.includes(p.plan))
      .map((p) => p.name);
    const shorten = (names: string[], all: number, noun: string): string => {
      if (names.length === 0) return `No ${noun}`;
      if (names.length >= all) return `All ${noun}s`;

      return names.length <= 2 ? names.join(', ') : `${names.length} ${noun}s`;
    };

    const roleText = shorten(roleNames, roleCount, 'role');
    const planText = this.targetSubscriptionTypes
      ? shorten(planNames, announcementAudiencePlans.length, 'plan')
      : 'All plans';
    const languageText = this.targetLanguages
      ? this.targetLanguages.map((code) => code.toUpperCase()).join(' ') || 'No language'
      : 'All languages';

    return `${roleText} · ${planText} · ${languageText}`;
  }

  getLanguagesText(): string {
    return this.languageCodes
      .map((code) => (this.unreviewedLanguageCodes.includes(code) ? `${code.toUpperCase()} (AI)` : code.toUpperCase()))
      .join(' ');
  }
}

//
// What a reader sees of an announcement in the list
//
export class AnnouncementListItem {
  announcementId: number;

  announcementType: AnnouncementType;

  title: string;

  summary?: string;

  coverImageUrl?: string;

  publishOn?: Date;

  isRead: boolean;

  //the language that is actually shown
  languageCode: string;

  constructor() {
    this.announcementId = 0;
    this.announcementType = AnnouncementType.News;
    this.title = '';
    this.isRead = false;
    this.languageCode = announcementEnglishCode;
  }
}

//what a reader sees of one announcement: the list fields and the body
export class AnnouncementDetailItem extends AnnouncementListItem {
  //HTML, with finished image URLs
  body: string;

  constructor() {
    super();
    this.body = '';
  }
}

export class AnnouncementSummary {
  hasNew: boolean;

  constructor() {
    this.hasNew = false;
  }
}

export class AnnouncementImageUpload {
  imageId: string;

  url: string;

  constructor() {
    this.imageId = '';
    this.url = '';
  }
}
